import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, ExternalLink, Clock, CheckCircle } from 'lucide-react';

// Import provider logos
const dropboxLogo = '/lovable-uploads/fe4eae71-a9b9-412e-a01b-aca507fbc687.png';
const googleLogo = '/lovable-uploads/e42a48b4-7ee2-4942-a97c-6ec2107feed9.png';
const microsoftLogo = '/lovable-uploads/c1562454-a1bd-4e98-9ba8-17a8648e1263.png';
const slackLogo = '/lovable-uploads/82dc0a51-bb90-4764-89c1-cbd334e1db99.png';
const xeroLogo = '/lovable-uploads/66212917-e096-4c06-b265-9dc1d2a8bb84.png';

// Provider configuration - only Microsoft and Dropbox
const PROVIDER_CAPABILITIES = {
  microsoft: {
    name: 'Microsoft',
    logo: microsoftLogo,
    buttonLabel: 'Connect Microsoft – Calendar Access'
  },
  dropbox: {
    name: 'Dropbox',
    logo: dropboxLogo,
    buttonLabel: 'Connect Dropbox – Files'
  }
};

const MANAGEMENT_LINKS = {
  google: 'https://myaccount.google.com/permissions',
  microsoft: 'https://myaccount.microsoft.com/consents'
};

const Authentication: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [businessId, setBusinessId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [connectionStatus, setConnectionStatus] = useState({
    microsoft: false,
    dropbox: false
  });
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Prefill Business ID from URL parameter
    const orgParam = searchParams.get('org');
    if (orgParam) {
      setBusinessId(orgParam);
    }

    // Check for success return parameters
    const connectedProvider = searchParams.get('connected');
    const status = searchParams.get('status');
    const org = searchParams.get('org');
    
    if (status === 'success' && connectedProvider && org) {
      setSuccessMessage(`Connected to ${connectedProvider} for ${org}. You can add more.`);
      setShowSuccessBanner(true);
      
      // Update connection status for the successful provider
      if (connectedProvider === 'microsoft' || connectedProvider === 'dropbox') {
        setConnectionStatus(prev => ({
          ...prev,
          [connectedProvider]: true
        }));
      }
    }
  }, [searchParams]);

  const handleConnect = (providerId: string) => {
    // Validate Business ID
    if (!businessId.trim()) {
      setErrors({ businessId: 'Enter your Business ID first.' });
      return;
    }
    
    // Clear errors
    setErrors({});
    
    const trimmedBusinessId = encodeURIComponent(businessId.trim());
    let oauthUrl: string;
    
    if (providerId === 'microsoft') {
      // Microsoft OAuth URL with proper parameters
      const baseUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
      const params = new URLSearchParams({
        client_id: 'cbb0fec9-1b20-47d6-908b-e939b97f420c',
        response_type: 'code',
        redirect_uri: 'https://obdggmcxrswxhpoozqcl.supabase.co/functions/v1/oauth/callback/microsoft',
        scope: 'offline_access Calendars.ReadWrite',
        state: trimmedBusinessId
      });
      oauthUrl = `${baseUrl}?${params.toString()}`;
    } else if (providerId === 'dropbox') {
      // Dropbox OAuth URL with proper parameters
      const baseUrl = 'https://www.dropbox.com/oauth2/authorize';
      const params = new URLSearchParams({
        client_id: 'j9ffggdvw90342l',
        response_type: 'code',
        redirect_uri: 'https://obdggmcxrswxhpoozqcl.supabase.co/functions/v1/oauth/callback/dropbox',
        token_access_type: 'offline',
        state: trimmedBusinessId
      });
      oauthUrl = `${baseUrl}?${params.toString()}`;
    } else {
      return; // Only Microsoft and Dropbox are supported
    }
    
    // Open in new tab
    window.open(oauthUrl, '_blank');
  };

  const renderProviderCard = (providerId: string) => {
    const provider = PROVIDER_CAPABILITIES[providerId as keyof typeof PROVIDER_CAPABILITIES];

    return (
      <Card key={providerId} className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <img 
              src={provider.logo} 
              alt={`${provider.name} logo`}
              className="w-8 h-8 object-contain"
            />
            <span className="text-brand-navy">{provider.name}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={() => handleConnect(providerId)}
            disabled={!businessId.trim()}
            className="w-full"
            variant="outline"
          >
            {provider.buttonLabel}
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Add meta tags for no indexing */}
      <div style={{ display: 'none' }}>
        <meta name="robots" content="noindex,nofollow" />
      </div>
      
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-brand-navy mb-4">
            Connect your apps
          </h1>
          <p className="text-lg text-muted-foreground mb-3">
            Authorize only the access required for your automations. You can revoke access anytime.
          </p>
          <p className="text-sm text-muted-foreground">
            This page is private to you. If we need added permissions later, we'll send you back here.
          </p>
        </div>

        {/* Success Banner */}
        {showSuccessBanner && (
          <Alert className="mb-8 border-green-200 bg-green-50 text-green-800">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription className="font-medium">
              {successMessage}
            </AlertDescription>
          </Alert>
        )}

        {/* Business ID Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-brand-teal" />
              Business ID (Client Code)
            </CardTitle>
            <CardDescription>
              We use this to link your connection to the right account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="business-id">Business ID</Label>
              <Input
                id="business-id"
                value={businessId}
                onChange={(e) => {
                  setBusinessId(e.target.value);
                  if (errors.businessId) {
                    setErrors(prev => ({ ...prev, businessId: '' }));
                  }
                }}
                placeholder="Enter your Business ID"
                className={errors.businessId ? 'border-destructive' : ''}
              />
              {errors.businessId && (
                <p className="text-sm text-destructive">{errors.businessId}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Provider Cards Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-brand-navy mb-6">
            Available Providers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.keys(PROVIDER_CAPABILITIES).map(renderProviderCard)}
          </div>
        </div>

        {/* Connection Status */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Connection Status</CardTitle>
            <CardDescription>
              Once connected, this status will update after we process your authorization. 
              If you revoke access, automations pause until you reconnect.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                {connectionStatus.microsoft ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                )}
                <span>Microsoft: {connectionStatus.microsoft ? 'Connected' : 'Not connected'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {connectionStatus.dropbox ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                )}
                <span>Dropbox: {connectionStatus.dropbox ? 'Connected' : 'Not connected'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manage Access */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Manage / Revoke Access</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Microsoft permissions</span>
                <a 
                  href={MANAGEMENT_LINKS.microsoft} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-brand-teal hover:text-brand-blue text-sm flex items-center"
                >
                  Manage Microsoft consents
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Revoking is instant. We'll prompt you to reconnect if required.
            </p>
          </CardContent>
        </Card>

        {/* Privacy Assurance */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-brand-teal" />
            <span className="font-medium text-brand-navy">Privacy & Security</span>
          </div>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            We request the minimum access needed ('least privilege'). Usage is logged per workflow. 
            You can revoke access at any time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Authentication;